import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Clock, Shield, Star, User as UserIcon } from "lucide-react";
import toast from "react-hot-toast";
import confetti from "canvas-confetti";

import { User } from "../types";
import { getUserById, createNotification } from "../services/userService";
import { updateApplicationStatus } from "../services/eventService";
import { trackVybeAcceptance, refundVybeAcceptance } from "../utils/vybeLimits";

type Status = "pending" | "accepted" | "rejected" | "waitlisted" | "withdrawn";

type AppItem = {
  id: string;
  eventId: string;
  applicantId: string;
  status: Status;
  message?: string | null;
  createdAt: string;
};

type Props = {
  applications: AppItem[];
  event: { id: string; title: string; posterId: string };
  onChange?: () => void;
  onMessageApplicant?: (applicantId: string) => void;
  onViewProfile?: (applicantId: string) => void;
};

const TABS: { key: "pending" | "accepted" | "rejected"; label: string; icon: React.ElementType }[] = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "accepted", label: "Accepted", icon: Check },
  { key: "rejected", label: "Passed", icon: X },
];

export default function ApplicationList({
  applications,
  event,
  onChange,
  onMessageApplicant,
  onViewProfile,
}: Props) {
  const [tab, setTab] = useState<"pending" | "accepted" | "rejected">("pending");
  const [users, setUsers] = useState<Record<string, User | null>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedBioId, setExpandedBioId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const ids = [...new Set(applications.map(a => a.applicantId))];
      const map: Record<string, User | null> = {};
      for (const id of ids) {
        try {
          map[id] = await getUserById(id);
        } catch {
          map[id] = null;
        }
      }
      setUsers(map);
      setIsLoading(false);
    })();
  }, [applications.length, applications.map(a => a.id).join(',')]);

  const counts = useMemo(() => ({
    pending: applications.filter(a => a.status === "pending").length,
    accepted: applications.filter(a => a.status === "accepted").length,
    rejected: applications.filter(a => a.status === "rejected").length,
  }), [applications]);

  const filtered = useMemo(() => applications.filter(a => a.status === tab), [applications, tab]);

  const fireConfetti = () =>
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });

  const updateLocal = async (id: string, status: Status) => {
    setBusyId(id);
    try {
      const app = applications.find(a => a.id === id);
      const wasAccepted = app?.status === "accepted";

      await updateApplicationStatus(id, status);

      if (app && status === "accepted") {
        const u = users[app.applicantId];
        if (u) {
          await createNotification({
            userId: u.id,
            type: "application_accepted",
            message: `Your application to "${event.title}" has been accepted!`,
            relatedId: event.id,
          });

          await trackVybeAcceptance(app.applicantId);
        }
      }

      if (app && wasAccepted && status === "rejected") {
        await refundVybeAcceptance(app.applicantId);
      }

      onChange?.();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't update right now");
    } finally {
      setBusyId(null);
    }
  };

  const onAccept = async (id: string) => {
    await updateLocal(id, "accepted");
    fireConfetti();
    toast.success("Accepted! Go to the chat (now in Messages) to arrange the finer details.");
  };

  const onReject = async (id: string) => {
    await updateLocal(id, "rejected");
    toast("Not this time");
  };

  if (isLoading) {
    return (
      <div className="bg-theme-cards rounded-2xl shadow-card p-8">
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl border border-theme-cards bg-black/20 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!applications.length) {
    return (
      <div className="bg-theme-cards rounded-2xl shadow-card p-8 text-center">
        <p className="text-white/70">No applications for this Vybe yet.</p>
      </div>
    );
  }

  return (
    <section className="bg-theme-cards rounded-2xl border border-theme-cards shadow-card p-4 sm:p-6">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center">
          <div className="w-1 h-6 bg-theme-primary rounded-full mr-3"></div>
          <h2 className="text-lg sm:text-xl font-sora font-bold text-white">Vybe Requests</h2>
        </div>
      </header>

      <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          const count = counts[key];
          return (
            <motion.button
              key={key}
              onClick={() => setTab(key)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={[
                "flex-1 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl transition-all font-semibold text-[10px] sm:text-sm flex items-center justify-center gap-1 sm:gap-2 relative",
                active
                  ? "bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg"
                  : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10"
              ].join(" ")}
            >
              <Icon className="h-3 w-3 sm:h-4 sm:w-4" strokeWidth={2.5} />
              <span className="hidden xs:inline sm:inline">{label}</span>
              {count > 0 && (
                <span className={`px-1 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-xs font-bold ${
                  active ? "bg-white/30 text-white" : "bg-pink-500 text-white"
                }`}>
                  {count}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((app) => (
            <RequestCard
              key={app.id}
              application={app}
              user={users[app.applicantId]}
              busy={busyId === app.id}
              onAccept={() => onAccept(app.id)}
              onReject={() => onReject(app.id)}
              currentTab={tab}
              isBioExpanded={expandedBioId === app.applicantId}
              onToggleBio={() => setExpandedBioId(expandedBioId === app.applicantId ? null : app.applicantId)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-black/20 p-8 text-center">
          <p className="text-white/70">
            {tab === "pending" ? "No pending requests" :
             tab === "accepted" ? "No accepted requests" : "No passed requests"}
          </p>
        </div>
      )}
    </section>
  );
}

function RequestCard({
  application,
  user,
  busy,
  onAccept,
  onReject,
  currentTab,
  isBioExpanded,
  onToggleBio,
}: {
  application: AppItem;
  user: User | null | undefined;
  busy: boolean;
  onAccept: () => void;
  onReject: () => void;
  currentTab: "pending" | "accepted" | "rejected";
  isBioExpanded: boolean;
  onToggleBio: () => void;
}) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all"
    >
      {/* Main content - always visible */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          {/* Avatar with verified badge */}
          <div className="relative flex-shrink-0">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full overflow-hidden bg-gradient-to-br from-pink-500/20 to-cyan-500/20 flex items-center justify-center border-2 border-white/10">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.username} className="h-full w-full object-cover" />
              ) : (
                <UserIcon className="h-7 w-7 sm:h-8 sm:w-8 text-white/60" />
              )}
            </div>
            {user?.isVerified && (
              <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center ring-2 ring-[#0A0B0F] shadow-lg">
                <Shield className="h-3.5 w-3.5 text-white" fill="currentColor" />
              </div>
            )}
          </div>

          {/* User info and actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <h3 className="font-bold text-white text-sm sm:text-base truncate">
                    {user?.username || "Member"}
                  </h3>
                  {user?.isVerified && (
                    <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 rounded-full flex-shrink-0">
                      <Shield className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-400" fill="currentColor" />
                      <span className="text-[9px] sm:text-[10px] font-semibold text-blue-400 uppercase">Verified</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-white/50 mt-0.5 sm:mt-1">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  <span>{formatDate(application.createdAt)}</span>
                  {user?.rating && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-0.5 sm:gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                        <span className="text-yellow-400 font-semibold">{user.rating.toFixed(1)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Actions for pending - desktop only */}
              {currentTab === "pending" && (
                <div className="hidden sm:flex gap-2 flex-shrink-0">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onReject}
                    disabled={busy}
                    className="h-10 w-10 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 flex items-center justify-center transition-all disabled:opacity-50"
                    title="Pass"
                  >
                    {busy ? <Spinner /> : <X className="h-4 w-4" strokeWidth={2.5} />}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onAccept}
                    disabled={busy}
                    className="h-10 w-10 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white flex items-center justify-center transition-all disabled:opacity-50 shadow-lg"
                    title="Accept"
                  >
                    {busy ? <Spinner /> : <Check className="h-4 w-4" strokeWidth={2.5} />}
                  </motion.button>
                </div>
              )}
            </div>

            {/* Bio - expandable if present */}
            {user?.bio && (
              <div className="mt-2 sm:mt-3">
                <div className="p-2 sm:p-2.5 bg-white/5 border border-white/10 rounded-lg">
                  <p className={`text-[11px] sm:text-xs text-white/80 leading-relaxed ${!isBioExpanded && user.bio.length > 100 ? 'line-clamp-3' : ''}`}>
                    {user.bio}
                  </p>
                </div>
                {user.bio.length > 100 && (
                  <button
                    onClick={onToggleBio}
                    className="mt-1 text-[10px] sm:text-xs text-pink-400 hover:text-pink-300 font-medium transition-colors"
                  >
                    {isBioExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            )}

            {/* Interests - always visible */}
            {user?.interests && user.interests.length > 0 && (
              <div className="mt-2 sm:mt-3">
                <div className="flex flex-wrap gap-1 sm:gap-1.5">
                  {user.interests.slice(0, 6).map((interest: string) => {
                    const emoji = interest.match(/^(\p{Emoji}+)/u)?.[0] || '';
                    const text = interest.replace(/^(\p{Emoji}+\s*)/u, '');
                    return (
                      <span
                        key={interest}
                        className="text-[10px] sm:text-[11px] bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-lg px-1.5 sm:px-2 py-0.5 sm:py-1 text-white/90 font-medium"
                      >
                        {emoji && <span className="mr-0.5 sm:mr-1">{emoji}</span>}
                        {text}
                      </span>
                    );
                  })}
                  {user.interests.length > 6 && (
                    <span className="text-[10px] sm:text-[11px] bg-white/5 border border-white/10 rounded-lg px-1.5 sm:px-2 py-0.5 sm:py-1 text-white/60 font-medium">
                      +{user.interests.length - 6}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Mobile actions for pending */}
            {currentTab === "pending" && (
              <div className="flex sm:hidden gap-2 mt-2.5 -mx-0.5">
                <button
                  onClick={onReject}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-lg bg-red-500/20 active:bg-red-500/30 border border-red-500/40 text-red-300 flex items-center justify-center gap-1.5 font-semibold text-xs transition-all disabled:opacity-50 min-h-[44px]"
                >
                  {busy ? <Spinner /> : (
                    <>
                      <X className="h-4 w-4" strokeWidth={2.5} />
                      <span>Pass</span>
                    </>
                  )}
                </button>
                <button
                  onClick={onAccept}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 active:from-red-600 active:to-pink-700 text-white flex items-center justify-center gap-1.5 font-semibold text-xs transition-all disabled:opacity-50 shadow-lg min-h-[44px]"
                >
                  {busy ? <Spinner /> : (
                    <>
                      <Check className="h-4 w-4" strokeWidth={2.5} />
                      <span>Accept</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions for accepted/rejected tabs */}
      {currentTab !== "pending" && (
        <div className="p-3 sm:p-4 border-t border-white/5">
          <div className="flex gap-2">
            {currentTab === "accepted" ? (
              <button
                onClick={onReject}
                disabled={busy}
                className="flex-1 text-sm py-2.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy ? <Spinner /> : (
                  <>
                    <X className="h-4 w-4" />
                    <span>Move to Passed</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onAccept}
                disabled={busy}
                className="flex-1 text-sm py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
              >
                {busy ? <Spinner /> : (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Move to Accepted</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function Spinner() {
  return <div className="animate-spin h-4 w-4 border-2 border-white/60 border-t-transparent rounded-full" />;
}
